import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import api from '@/lib/api';
import { applyBrandColor, clearFirmThemeCache } from '@/utils/theme';

/**
 * Loads the firm's brand_color when the user is authenticated and applies the
 * palette derived from it (see themeFromBrandColor) as CSS custom properties.
 *
 * Place this inside the router but outside individual routes so it runs once
 * after login.
 */
export function FirmThemeProvider({ children }: { children: React.ReactNode }) {
  const { user, token } = useAuth();
  const appliedFirmId = useRef<number | null>(null);

  useEffect(() => {
    if (!token || !user) {
      // User logged out — clear the cached firm theme; the login page falls
      // back to the Sentra defaults in index.css on its next load.
      clearFirmThemeCache();
      appliedFirmId.current = null;
      return;
    }

    const firmId = user.firms?.[0];
    if (!firmId || firmId === appliedFirmId.current) return;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get(`/api/firms/${firmId}/`);
        if (cancelled) return;

        applyBrandColor(data.brand_color);
        appliedFirmId.current = firmId;
      } catch {
        // Theme loading is non-critical — silently fail
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, user]);

  return <>{children}</>;
}
